// Switch.tsx
import React from "react";
import { cn } from "@/utils/cn";
import { SwitchProps, SwitchVariant } from "./Switch.types";
import { SwitchBase } from "@/components/ui/switch";

const baseRoot =
    "inline-flex items-center rounded-full p-1 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2";

const sizeStyles: Record<NonNullable<SwitchProps["size"]>, string> = {
    sm: "h-5 w-9",
    md: "h-6 w-11",
    lg: "h-8 w-14",
};

// -> clases que aplicamos cuando data-[state=checked] (estado activo)
const checkedStyles: Record<SwitchVariant, string> = {
    default: "data-[state=checked]:bg-gray-300 dark:data-[state=checked]:bg-gray-700 data-[state=checked]:text-white",
    primary: "data-[state=checked]:bg-primary-500 dark:data-[state=checked]:bg-primary-500",
    secondary: "data-[state=checked]:bg-secondary-500 dark:data-[state=checked]:bg-secondary-500",
    success: "data-[state=checked]:bg-green-500 dark:data-[state=checked]:bg-green-500",
    error: "data-[state=checked]:bg-red-500 dark:data-[state=checked]:bg-red-500",
    warning: "data-[state=checked]:bg-yellow-500 dark:data-[state=checked]:bg-yellow-500",
    info: "data-[state=checked]:bg-blue-500 dark:data-[state=checked]:bg-blue-500",
    darkInfo: "data-[state=checked]:bg-blue-700 dark:data-[state=checked]:bg-blue-700",
    darkError: "data-[state=checked]:bg-red-700 dark:data-[state=checked]:bg-red-700",
};

// -> clases que aplicamos cuando data-[state=unchecked] (estado inactivo)
const uncheckedStyles: Record<SwitchVariant, string> = {
    default: "data-[state=unchecked]:bg-gray-300 dark:data-[state=unchecked]:bg-gray-700 data-[state=unchecked]:text-white",
    primary: "data-[state=unchecked]:bg-primary-500 dark:data-[state=unchecked]:bg-primary-500",
    secondary: "data-[state=unchecked]:bg-secondary-500 dark:data-[state=unchecked]:bg-secondary-500",
    success: "data-[state=unchecked]:bg-green-500 dark:data-[state=unchecked]:bg-green-500",
    error: "data-[state=unchecked]:bg-red-500 dark:data-[state=unchecked]:bg-red-500",
    warning: "data-[state=unchecked]:bg-yellow-500 dark:data-[state=unchecked]:bg-yellow-500",
    info: "data-[state=unchecked]:bg-blue-500 dark:data-[state=unchecked]:bg-blue-500",
    darkInfo: "data-[state=unchecked]:bg-blue-700 dark:data-[state=unchecked]:bg-blue-700",
    darkError: "data-[state=unchecked]:bg-red-700 dark:data-[state=unchecked]:bg-red-700",
};

export const Switch: React.FC<SwitchProps> = ({
    label,
    variant = "primary",
    inactiveVariant = "default",
    checked,
    onCheckedChange,
    className,
    size = "md",
    disabled = false,
}) => {
    const rootClass = cn(
        baseRoot,
        sizeStyles[size],
        checkedStyles[variant],
        uncheckedStyles[inactiveVariant],
        className
    );

    return (
        <div className="flex items-center gap-2">
            <SwitchBase checked={checked} onCheckedChange={onCheckedChange} className={rootClass} disabled={disabled} />
            {label && <span className="text-sm font-medium select-none">{label}</span>}
        </div>
    );
};

export default Switch;
